import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  CheckCircle2,
  Check,
  Upload,
  X,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { LEAD } from "@/constants/testIds";

const BUILD_PACKAGES = [
  { id: "starter", name: "Starter", price: 99, blurb: "Up to 3 pages" },
  { id: "business", name: "Business", price: 149, blurb: "Up to 6 pages", popular: true },
  { id: "premium", name: "Premium", price: 199, blurb: "10+ pages · E-com" },
];

const MAINT_PACKAGES = [
  { id: "care_starter", name: "Starter Care", price: 49, blurb: "After 12 months" },
  { id: "care_business", name: "Business Care", price: 79, blurb: "After 12 months", popular: true },
  { id: "care_premium", name: "Premium Care", price: 99, blurb: "After 12 months" },
];

const MAX_LOGO_MB = 3;

function PackageCard({ pkg, selected, onToggle, testid, suffix }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(pkg.id)}
      data-testid={testid}
      className={`relative text-left rounded-2xl border-2 p-4 transition-all ${
        selected
          ? "border-[#CCFF00] bg-[#CCFF00]/5 shadow-[0_0_24px_rgba(204,255,0,0.18)]"
          : "border-[#27272A] bg-[#111] hover:border-white/30"
      }`}
    >
      {pkg.popular && (
        <span className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-[#CCFF00] text-black text-[9px] font-bold uppercase tracking-widest">
          Popular
        </span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-base font-bold">{pkg.name}</div>
          <div className="font-display text-2xl font-black mt-0.5">
            ${pkg.price}
            <span className="text-xs text-zinc-500 font-medium ml-1">
              {suffix}
            </span>
          </div>
          <div className="text-xs text-zinc-400 mt-1">{pkg.blurb}</div>
        </div>
        <div
          className={`shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
            selected
              ? "bg-[#CCFF00] border-[#CCFF00]"
              : "border-zinc-600 bg-transparent"
          }`}
        >
          {selected && <Check className="w-4 h-4 text-black" strokeWidth={3} />}
        </div>
      </div>
    </button>
  );
}

export default function LeadFormDialog({ open, onOpenChange }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    project_details: "",
    reference_website: "",
    selected_build_package: "",
    selected_maintenance_package: "",
    logo_data_url: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const toggleBuild = (id) =>
    setForm((s) => ({
      ...s,
      selected_build_package: s.selected_build_package === id ? "" : id,
    }));
  const toggleMaint = (id) =>
    setForm((s) => ({
      ...s,
      selected_maintenance_package:
        s.selected_maintenance_package === id ? "" : id,
    }));

  const onLogoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error("Please upload an image file.");
      return;
    }
    if (f.size > MAX_LOGO_MB * 1024 * 1024) {
      toast.error(`Logo must be under ${MAX_LOGO_MB}MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () =>
      setForm((s) => ({ ...s, logo_data_url: String(reader.result || "") }));
    reader.readAsDataURL(f);
  };

  const clearLogo = () => {
    setForm((s) => ({ ...s, logo_data_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    try {
      setLoading(true);
      await createLead({
        ...form,
        selected_build_package: form.selected_build_package || null,
        selected_maintenance_package:
          form.selected_maintenance_package || null,
      });
      setDone(true);
      toast.success("Got it! We'll reach out within 1 business day.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send. Try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({
      name: "",
      email: "",
      phone: "",
      business_name: "",
      project_details: "",
      reference_website: "",
      selected_build_package: "",
      selected_maintenance_package: "",
      logo_data_url: "",
    });
  };

  const hasSelection =
    !!form.selected_build_package || !!form.selected_maintenance_package;
  const ctaLabel = "Start Building";
  const subhead = hasSelection
    ? "Great choice — drop your details and we'll kick off your project."
    : "Fill in a few details and we'll send a free custom quote within 1 business day.";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setTimeout(reset, 250);
      }}
    >
      <DialogContent className="bg-[#0a0a0a] border-[#27272A] text-white sm:max-w-[640px] max-h-[92vh] overflow-y-auto">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-black tracking-tight">
                {hasSelection ? (
                  <>
                    Let&apos;s <span className="text-gradient-neon">start building.</span>
                  </>
                ) : (
                  <>
                    Let&apos;s <span className="text-gradient-neon">build it.</span>
                  </>
                )}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {subhead}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-5 mt-2">
              {/* Contact */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb">
                    Name *
                  </Label>
                  <Input
                    data-testid={LEAD.name}
                    value={form.name}
                    onChange={setField("name")}
                    placeholder="Jane Doe"
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] focus-visible:border-[#CCFF00]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb">
                    Email *
                  </Label>
                  <Input
                    data-testid={LEAD.email}
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="you@business.com"
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] focus-visible:border-[#CCFF00]"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb">
                    Phone
                  </Label>
                  <Input
                    data-testid={LEAD.phone}
                    value={form.phone}
                    onChange={setField("phone")}
                    placeholder="(555) 123-4567"
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] focus-visible:border-[#CCFF00]"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb">
                    Business Name
                  </Label>
                  <Input
                    data-testid={LEAD.business}
                    value={form.business_name}
                    onChange={setField("business_name")}
                    placeholder="Acme Co."
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] focus-visible:border-[#CCFF00]"
                  />
                </div>
              </div>

              {/* Build packages */}
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#CCFF00]" />
                  Website Plan ($99–$199/mo · 12 months)
                </Label>
                <div className="grid sm:grid-cols-3 gap-2 mt-2">
                  {BUILD_PACKAGES.map((p) => (
                    <PackageCard
                      key={p.id}
                      pkg={p}
                      selected={form.selected_build_package === p.id}
                      onToggle={toggleBuild}
                      testid={`lead-build-pkg-${p.id}`}
                      suffix="/mo"
                    />
                  ))}
                </div>
              </div>

              {/* Maintenance packages */}
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#CCFF00]" />
                  Website Care (optional · after 12 months)
                </Label>
                <div className="grid sm:grid-cols-3 gap-2 mt-2">
                  {MAINT_PACKAGES.map((p) => (
                    <PackageCard
                      key={p.id}
                      pkg={p}
                      selected={form.selected_maintenance_package === p.id}
                      onToggle={toggleMaint}
                      testid={`lead-maint-pkg-${p.id}`}
                      suffix="/mo"
                    />
                  ))}
                </div>
              </div>

              {/* Reference site */}
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb">
                  Reference Website
                </Label>
                <Input
                  data-testid="lead-input-reference"
                  type="url"
                  value={form.reference_website}
                  onChange={setField("reference_website")}
                  placeholder="https://a-site-you-love.com"
                  className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] focus-visible:border-[#CCFF00]"
                />
                <p className="mt-1 text-xs text-zinc-500">
                  Optional — paste a link to a site you&apos;d like yours to look like.
                </p>
              </div>

              {/* Logo upload */}
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb">
                  Your Logo (optional)
                </Label>
                {!form.logo_data_url ? (
                  <button
                    type="button"
                    data-testid="lead-logo-upload-btn"
                    onClick={() => fileRef.current?.click()}
                    className="mt-1 w-full rounded-lg border-2 border-dashed border-[#27272A] hover:border-[#CCFF00] hover:bg-[#CCFF00]/5 transition-all p-6 flex flex-col items-center justify-center text-zinc-400 hover:text-[#CCFF00]"
                  >
                    <Upload className="w-6 h-6 mb-2" />
                    <span className="text-sm font-medium">
                      Click to upload your logo
                    </span>
                    <span className="text-xs text-zinc-500 mt-1">
                      PNG, JPG, SVG · up to {MAX_LOGO_MB}MB
                    </span>
                  </button>
                ) : (
                  <div className="mt-1 flex items-center gap-4 bg-[#111] border border-[#27272A] rounded-lg p-3">
                    <img
                      src={form.logo_data_url}
                      alt="logo preview"
                      className="w-16 h-16 object-contain bg-black/30 rounded"
                    />
                    <div className="flex-1 text-sm">
                      <div className="font-semibold text-[#CCFF00]">
                        Logo attached
                      </div>
                      <div className="text-xs text-zinc-500">
                        Ready to send with your inquiry
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearLogo}
                      data-testid="lead-logo-clear-btn"
                      className="text-zinc-400 hover:text-red-400"
                      title="Remove logo"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onLogoChange}
                  className="hidden"
                  data-testid="lead-logo-input"
                />
              </div>

              {/* Project details */}
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-mono-jb">
                  Project Details
                </Label>
                <Textarea
                  data-testid={LEAD.details}
                  value={form.project_details}
                  onChange={setField("project_details")}
                  placeholder="Tell us about your business and goals…"
                  className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] focus-visible:border-[#CCFF00] min-h-[90px]"
                />
              </div>

              <Button
                type="submit"
                data-testid={LEAD.submit}
                disabled={loading}
                className="btn-neon rounded-none w-full h-12"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                  </>
                ) : (
                  ctaLabel
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#CCFF00]" />
            </div>
            <h3 className="font-display text-2xl font-bold mt-6">
              {hasSelection ? "We’re on it!" : "Quote request received!"}
            </h3>
            <p className="text-zinc-400 mt-2">
              We’ll be in touch within 1 business day.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="btn-neon rounded-none mt-8 px-8"
              data-testid="lead-close-success"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
